import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Typography,
  styled,
  TextField,
  Paper,
  Grid,
  Box,
  Tab,
  Tabs,
  Fab,
  MenuItem,
  Snackbar,
  Select,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Modal,
  Button,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import EditIcon from "@mui/icons-material/Edit";
import { serverTimestamp } from "firebase/firestore";
import AddIcon from "@mui/icons-material/Add";
import MuiAlert from "@mui/material/Alert";
import AdapterDayjs from "@mui/lab/AdapterDayjs";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import { SaveCloseDialog } from "../components/SaveCloseDialog";
import { Save, Close } from "@mui/icons-material";
import { doc, getDoc, or, setDoc, where, query, collection, getDocs, arrayUnion, arrayRemove, updateDoc, writeBatch, addDoc } from "firebase/firestore";
import { WorkTypes } from "../components/WorkTypes";
import JobSheetModal from "components/JobSheetModal";
import ProjectStatus from "./ProjectStatus";
import { Forms } from "../components/Forms";
import Notes from "../components/Notes";
import Documents from "../components/Documents";
import { Context } from "context/Wrapper";
import { db } from "config/config";
import { useFetchCollectionDocs } from "apis/dataSnapshots";
import InfiniteScrollSelect from "components/shared/InfiniteScrollSelect";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import ClearIcon from '@mui/icons-material/Clear';
import { useSubscriptionState } from "context/SubscriptionContext";
import { useFetchUserDefinedOptions } from "components/fetchUserDefinedOptions";
import "../styles/userDefinedOptions.css"
import { Inspections } from "components/Inspections";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Item = styled(Paper)(({ theme }) => {
  return {
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.primary.main,
    background: theme.palette.secondary.main,
  };
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

// Cache for expensive operations
const projectDataCache = new Map();
const buildingInfoCache = new Map();
const groupUsersCache = new Map();

export const ProjectDetail = (props) => {
  const context = useContext(Context);
  const currentSubscription = useSubscriptionState();

  // Extract context variables FIRST
  const { groupId, isPro, userId } = context?.state?.userProfile || {};
  let { showAllDataFlag } = context?.state || {};

  const [openModal, setOpenModal] = useState(false);
  const [loadingJob, setLoadingJob] = useState(false);
  const [previousJobData, setPreviousJobData] = useState(null);
  const [groupUsers, setGroupUsers] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  // Use refs for data that doesn't need re-renders
  const contactsCacheRef = useRef(new Map());
  const buildingsCacheRef = useRef(new Map());
  const abortControllerRef = useRef(null);
  const isSwitchingRef = useRef(false);

  // Now define syncTaskForJob AFTER groupId is available
  const syncTaskForJob = useCallback(async (job) => {
    if (!groupId) return;

    // Use setTimeout to prevent blocking main thread
    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const todosRef = collection(db, "todos");
          const allTodosSnap = await getDocs(
            query(todosRef, where("groupId", "==", groupId))
          );
          if (allTodosSnap.empty && !job.assignedTo) {
            resolve();
            return;
          }

          if (job.assignedTo) {
            const userDocSnap = allTodosSnap.docs.find(d => d.data().userId === job.assignedTo);
            let board = userDocSnap?.data().board || {
              columns: [
                { id: 1, title: "Backlog", cards: [] },
                { id: 2, title: "In Progress", cards: [] },
                { id: 3, title: "For Review", cards: [] },
                { id: 4, title: "Completed", cards: [] },
                { id: 5, title: "Archived", cards: [] },
              ]
            };

            const existingCol = board.columns.find(col => col.cards.some(c => c.id === job.id));
            const targetColId = (job.kanbanColumnId !== undefined && job.kanbanColumnId !== null)
              ? job.kanbanColumnId
              : existingCol?.id || board.columns[0].id;

            board = {
              ...board,
              columns: board.columns.map(col => ({
                ...col,
                cards: col.cards.filter(c => c.id !== job.id)
              }))
            };

            const targetCol = board.columns.find(c => c.id === targetColId) || board.columns[0];
            targetCol.cards.push({
              id: job.id,
              title: job.customerReference || "Untitled Job",
              description: "",
              details: job.workDescription || "",
              due: job.date || "",
              assignedTo: job.assignedTo,
              isJobTask: true,
            });

            if (userDocSnap) {
              await updateDoc(doc(db, "todos", userDocSnap.id), { board });
            } else {
              await addDoc(todosRef, {
                board,
                userId: job.assignedTo,
                groupId,
                sampleDeleted: false
              });
            }
          }

          // Process other users in chunks to prevent blocking
          const otherDocs = allTodosSnap.docs.filter(d => d.data().userId !== job.assignedTo);
          const chunkSize = 10;

          for (let i = 0; i < otherDocs.length; i += chunkSize) {
            const chunk = otherDocs.slice(i, i + chunkSize);
            await Promise.all(chunk.map(async (d) => {
              const b = d.data().board;
              const newBoard = {
                ...b,
                columns: b.columns.map(col => ({
                  ...col,
                  cards: col.cards.filter(c => c.id !== job.id)
                }))
              };
              await updateDoc(doc(db, "todos", d.id), { board: newBoard });
            }));

            // Yield to main thread every chunk
            await new Promise(resolve => setTimeout(resolve, 0));
          }

          resolve();
        } catch (error) {
          console.error("Error syncing task:", error);
          resolve();
        }
      }, 0);
    });
  }, [groupId]);

  const contacts = useFetchCollectionDocs({
    collectionName: "contacts",
    conditions:
      userId && groupId && isPro
        ? [or(where("groupId", "==", groupId), where("userId", "==", userId))]
        : userId
          ? [where("userId", "==", userId)]
          : [],
  });

  const buildings = useFetchCollectionDocs({
    collectionName: "buildings",
    conditions:
      userId && groupId && isPro
        ? [or(where("groupId", "==", groupId), where("userId", "==", userId))]
        : userId
          ? [where("userId", "==", userId)]
          : [],
  });

  // Cache contacts and buildings for faster lookups
  useEffect(() => {
    if (contacts.length > 0) {
      const cache = new Map();
      contacts.forEach(contact => {
        cache.set(contact.id, contact);
      });
      contactsCacheRef.current = cache;
    }
  }, [contacts]);

  useEffect(() => {
    if (buildings.length > 0) {
      const cache = new Map();
      buildings.forEach(building => {
        cache.set(building.id, building);
      });
      buildingsCacheRef.current = cache;
    }
  }, [buildings]);

  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projectDetail, setProjectDetail] = useState();
  const [projectBuildingDetails, setProjectBuildingDetails] = useState();

  const assignedToName = useMemo(() =>
    projectDetail?.assignedTo ? groupUsers.find(u => u.id === projectDetail.assignedTo)?.name || "" : "",
    [projectDetail?.assignedTo, groupUsers]
  );

  const [ownerDetails, setOwnerDetails] = useState(null);
  const [workTypes, setWorkTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentOptionFieldDisplayed, setCurrentOptionFieldDisplayed] = useState({ label: "", name: "" });
  const [userDefinedOption, setUserDefinedOption] = useState('');
  const [editingOption, setEditingOption] = useState(null);
  const [typeFieldOptions, setTypeFieldOptions] = useState([]);
  const [akaFieldOptions, setAkaFieldOptions] = useState([]);
  const [acpReportFieldOptions, setAcpReportFieldOptions] = useState([]);
  const [selfCertificationFieldOptions, setSelfCertificationFieldOptions] = useState([]);
  const [reconReqdFieldOptions, setReconReqdFieldOptions] = useState([]);
  const [flaggedMessage, setFlaggedMessage] = useState('This job requires attention');

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const getProject = useCallback(async (id) => {
    // Prevent multiple simultaneous switches
    if (isSwitchingRef.current) return;
    isSwitchingRef.current = true;

    // Check cache first
    const cacheKey = `project_${id}`;
    if (projectDataCache.has(cacheKey)) {
      setProjectDetail(projectDataCache.get(cacheKey));
      isSwitchingRef.current = false;
      return;
    }

    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const projectRef = doc(db, "projects", id);
      const initialProjectDetail = await getDoc(projectRef);

      if (!initialProjectDetail.exists()) {
        console.warn(`Project ${id} not found`);
        isSwitchingRef.current = false;
        return;
      }

      const projectData = {
        ...initialProjectDetail.data(),
        id: initialProjectDetail.id,
      };

      projectDataCache.set(cacheKey, projectData);

      setFlaggedMessage(projectData.flaggedMessage || 'This job requires attention');
      setProjectDetail(projectData);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching project:', error);
      }
    } finally {
      setTimeout(() => {
        isSwitchingRef.current = false;
      }, 100);
    }
  }, []);

  const updateLowercaseFields = useCallback(async (projectData) => {
    const cacheKey = `lowercase_${projectData.id}`;

    if (projectDataCache.has(cacheKey)) {
      return projectDataCache.get(cacheKey);
    }

    const clientDetails = contactsCacheRef.current.get(projectData?.client);
    const tenantDetails = contactsCacheRef.current.get(projectData?.tenant);
    const assignedToName = groupUsers.find(u => u.id === projectData?.assignedTo)?.name || "";
    const building = buildingsCacheRef.current.get(projectData.building) || {};

    const buildingString_lc = building.customerReference_lc || (building.customerReference || '').trim().toLowerCase();
    const buildingAddress = `${building.houseNumber || ''} ${building.streetName || ''}`.trim();
    const buildingAddress_lc = buildingAddress.toLowerCase();

    const result = {
      ...projectData,
      customerReference_lc: (projectData.customerReference || '').trim().toLowerCase(),
      status_lc: (projectData.status || '').trim().toLowerCase(),
      aka_lc: (projectData.aka || '').trim().toLowerCase(),
      client_lc: (clientDetails?.businessName || '').trim().toLowerCase(),
      tenant_lc: (tenantDetails?.businessName || '').trim().toLowerCase(),
      applicant_lc: (projectData.applicantName || '').trim().toLowerCase(),
      filingRep_lc: (projectData.filingRepName || '').trim().toLowerCase(),
      assignedTo_lc: (assignedToName || '').trim().toLowerCase(),
      buildingString_lc,
      buildingAddress_lc,
    };

    // Cache the result
    projectDataCache.set(cacheKey, result);

    return result;
  }, [groupUsers]);

  useEffect(() => {
    if (projectId) {
      getProject(projectId);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isSwitchingRef.current = false;
    };
  }, [projectId, getProject]);

  const applicantDetails = useMemo(() => {
    if (!projectDetail?.applicant) return undefined;
    return contactsCacheRef.current.get(projectDetail.applicant);
  }, [projectDetail?.applicant, contactsLoaded]);

  const filingRepDetails = useMemo(() => {
    if (!projectDetail?.filingRep) return undefined;
    return contactsCacheRef.current.get(projectDetail.filingRep);
  }, [projectDetail?.filingRep, contactsLoaded]);

  const clientDetails = useMemo(() =>
    projectDetail?.client ? contactsCacheRef.current.get(projectDetail.client) : undefined,
    [projectDetail?.client, contactsLoaded]
  );

  const tenantDetails = useMemo(() =>
    projectDetail?.tenant ? contactsCacheRef.current.get(projectDetail.tenant) : undefined,
    [projectDetail?.tenant, contactsLoaded]
  );

  const buildingDetails = useMemo(() => {
    if (!projectDetail?.building) return undefined;

    const cached = buildingsCacheRef.current.get(projectDetail.building);
    if (cached) return cached;

    return projectBuildingDetails;
  }, [projectDetail?.building, projectBuildingDetails]);


  useFetchUserDefinedOptions(
    ["type", "aka", "ACPReport", "selfCertification", "reconReqd"],
    [setTypeFieldOptions, setAkaFieldOptions, setAcpReportFieldOptions, setSelfCertificationFieldOptions, setReconReqdFieldOptions],
    [showAllDataFlag, groupId, userId],
    db,
    groupId,
    showAllDataFlag,
    userId
  );

  // Optimized owner details fetching with caching
  useEffect(() => {
    const fetchOwnerDetails = async () => {
      if (!buildingDetails?.owner) {
        setOwnerDetails(null);
        return;
      }

      const cacheKey = `owner_${buildingDetails.owner}`;
      if (buildingInfoCache.has(cacheKey)) {
        setOwnerDetails(buildingInfoCache.get(cacheKey));
        return;
      }

      try {
        const ownerRef = doc(db, "contacts", buildingDetails.owner);
        const ownerSnap = await getDoc(ownerRef);
        if (ownerSnap.exists()) {
          const ownerData = ownerSnap.data();
          buildingInfoCache.set(cacheKey, ownerData);
          setOwnerDetails(ownerData);
        } else {
          setOwnerDetails(null);
        }
      } catch (error) {
        console.error("Error fetching owner details:", error);
        setOwnerDetails(null);
      }
    };

    // Use setTimeout to prevent blocking
    setTimeout(fetchOwnerDetails, 0);
  }, [buildingDetails?.owner]);

  // Optimized workTypes fetching with chunked processing
  useEffect(() => {
    const cacheKey = `workTypes_${projectId}`;

    // Check cache first
    if (projectDataCache.has(cacheKey)) {
      setWorkTypes(projectDataCache.get(cacheKey));
      return;
    }

    const unsubscribe = db
      .collection("workTypes")
      .where("projectId", "==", projectId)
      .onSnapshot((snapshot) => {
        // Process in chunks to prevent blocking
        const processChunk = () => {
          const workTypesData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));

          // Cache the result
          projectDataCache.set(cacheKey, workTypesData);
          setWorkTypes(workTypesData);
        };

        // Use requestIdleCallback if available
        if ('requestIdleCallback' in window) {
          requestIdleCallback(processChunk);
        } else {
          setTimeout(processChunk, 0);
        }
      }, (error) => {
        console.error("Error fetching work types:", error);
      });

    return () => unsubscribe();
  }, [projectId]);

  // Optimized group users fetching with caching
  useEffect(() => {
    const fetchGroupUsers = async () => {
      if (!groupId) return;

      const cacheKey = `groupUsers_${groupId}`;
      if (groupUsersCache.has(cacheKey)) {
        setGroupUsers(groupUsersCache.get(cacheKey));
        return;
      }

      try {
        const q = query(
          collection(db, 'profiles'),
          where('groupId', '==', groupId)
        );

        const querySnapshot = await getDocs(q);
        const users = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Cache the result
        groupUsersCache.set(cacheKey, users);
        setGroupUsers(users);
      } catch (error) {
        console.error('Error fetching group users:', error);
      }
    };

    fetchGroupUsers();
  }, [groupId]);

  const getGroupInfo = useCallback(async (groupId) => {
    if (!groupId) return null;

    const cacheKey = `groupInfo_${groupId}`;
    if (projectDataCache.has(cacheKey)) {
      return projectDataCache.get(cacheKey);
    }

    try {
      const docSnap = await getDoc(doc(db, "groups", groupId));
      if (docSnap.exists()) {
        const groupData = docSnap.data();
        projectDataCache.set(cacheKey, groupData);
        return groupData;
      }
    } catch (err) {
      console.error("Failed to fetch group info", err);
    }
    return null;
  }, []);

  const [sheetData, setSheetData] = useState(null);

  const fetchContactById = useCallback(async (contactId) => {
    if (!contactId || contactId === "undefined") return null;

    // Check cache first
    if (contactsCacheRef.current.has(contactId)) {
      return contactsCacheRef.current.get(contactId);
    }

    try {
      const contactRef = doc(db, "contacts", contactId);
      const contactSnap = await getDoc(contactRef);

      if (contactSnap.exists()) {
        const contactData = { id: contactSnap.id, ...contactSnap.data() };
        contactsCacheRef.current.set(contactId, contactData);
        return contactData;
      }
    } catch (error) {
      console.error("Error fetching contact:", error);
    }
    return null;
  }, []);

  const fetchBuildingById = useCallback(async (buildingId) => {
    if (!buildingId || buildingId === "undefined") return null;

    // Check cache first
    if (buildingsCacheRef.current.has(buildingId)) {
      return buildingsCacheRef.current.get(buildingId);
    }

    try {
      const buildingRef = doc(db, "buildings", buildingId);
      const buildingSnap = await getDoc(buildingRef);

      if (buildingSnap.exists()) {
        const buildingData = { id: buildingSnap.id, ...buildingSnap.data() };
        buildingsCacheRef.current.set(buildingId, buildingData);
        return buildingData;
      }
    } catch (error) {
      console.error("Error fetching building:", error);
    }
    return null;
  }, []);

  const fetchOwnerById = useCallback(async (ownerId) => {
    if (!ownerId) return null;

    const cacheKey = `owner_${ownerId}`;
    if (buildingInfoCache.has(cacheKey)) {
      return buildingInfoCache.get(cacheKey);
    }

    try {
      const ownerRef = doc(db, "contacts", ownerId);
      const ownerSnap = await getDoc(ownerRef);

      if (ownerSnap.exists()) {
        const ownerData = ownerSnap.data();
        buildingInfoCache.set(cacheKey, ownerData);
        return ownerData;
      }
    } catch (error) {
      console.error("Error fetching owner:", error);
    }
    return null;
  }, []);

  // Add loading state for building details
  const [buildingDetailsLoading, setBuildingDetailsLoading] = useState(false);

  // Replace your existing getBuildingInfo function with this:
  const getBuildingInfo = useCallback(async (buildingId) => {
    if (!buildingId) {
      setProjectBuildingDetails(null);
      return;
    }

    setBuildingDetailsLoading(true);

    try {
      const buildingData = await fetchBuildingById(buildingId);

      if (!buildingData) {
        setProjectBuildingDetails(null);
        return;
      }

      let ownerName = "";
      if (buildingData.owner) {
        const ownerData = await fetchOwnerById(buildingData.owner);
        ownerName = ownerData?.fullName || "";
      }

      setProjectBuildingDetails({
        ...buildingData,
        owner: ownerName,
      });
    } catch (error) {
      console.error("Error in getBuildingInfo:", error);
      setProjectBuildingDetails(null);
    } finally {
      setBuildingDetailsLoading(false);
    }
  }, [fetchBuildingById, fetchOwnerById]);

  // Add this useEffect to fetch building details when project loads
  useEffect(() => {
    if (projectDetail?.building) {
      getBuildingInfo(projectDetail.building);
    }
  }, [projectDetail?.building, getBuildingInfo]);

  // Remove the old useEffect that had setTimeout (lines 340-344)
  // Delete these lines:
  // useEffect(() => {
  //   if (projectDetail?.building) {
  //     setTimeout(() => {
  //       getBuildingInfo(projectDetail.building);
  //     }, 0);
  //   }
  // }, [projectDetail?.building]);
  useEffect(() => {
    const fetchProjectContacts = async () => {
      if (!projectDetail) return;

      setContactsLoading(true);
      setContactsLoaded(false); // Reset loaded state
      console.log("Fetching contacts for project:", projectDetail.customerReference);

      const contactIds = [
        projectDetail.client,
        projectDetail.tenant,
        projectDetail.applicant,
        projectDetail.filingRep,
        projectDetail.projectManager,
        projectDetail.contractor
      ].filter(id => id && id !== "undefined");

      if (contactIds.length > 0) {
        console.log("Contact IDs to fetch:", contactIds);
        // Fetch all contacts in parallel
        await Promise.all(contactIds.map(id => fetchContactById(id)));
        console.log("Contacts cache now has:", Array.from(contactsCacheRef.current.keys()));
      }
      setContactsLoading(false);
      setContactsLoaded(true); // Mark as loaded to trigger re-render
    };

    fetchProjectContacts();
  }, [projectDetail, fetchContactById]);
  // Optimized sheet data generation
  useEffect(() => {
    const generateSheetData = async () => {
      if (!projectDetail || !groupId) return;

      try {
        const groupInfo = await getGroupInfo(groupId);
        const tenantDetails = contactsCacheRef.current.get(projectDetail?.tenant);
        const clientDetails = contactsCacheRef.current.get(projectDetail?.client);
        const projectManagerDetails = contactsCacheRef.current.get(projectDetail?.projectManager);
        const contractorDetails = contactsCacheRef.current.get(projectDetail?.contractor);

        const adjustedWorkTypes = workTypes.map((work) => {
          const applicantDetails = contactsCacheRef.current.get(work.applicant);
          return {
            workType: work.workType,
            estimatedCost: work.estimatedCost,
            dateCreated: work.dateCreated,
            applicantDetails: applicantDetails || null,
          };
        });

        const formatValues = (...values) => values.filter(v => v !== "").join(", ") || null;

        const sheetDataResult = {
          jobNo: projectDetail?.customerReference || null,
          address: formatValues(
            buildingDetails?.houseNumber,
            buildingDetails?.streetName,
            buildingDetails?.city,
            buildingDetails?.state,
            buildingDetails?.ZIP
          ),
          floors: projectDetail?.floors || null,
          projectManger: projectManagerDetails?.fullName || null,
          owner: ownerDetails?.businessName || null,
          client: formatValues(
            clientDetails?.fullName,
            clientDetails?.businessName,
            clientDetails?.address1,
            clientDetails?.city,
            clientDetails?.state,
            clientDetails?.ZIP,
            clientDetails?.phone,
            clientDetails?.email ? `Email: ${clientDetails.email}` : ""
          ),
          contractor: formatValues(
            contractorDetails?.fullName,
            contractorDetails?.businessName,
            contractorDetails?.address1,
            contractorDetails?.city,
            contractorDetails?.state,
            contractorDetails?.ZIP,
            contractorDetails?.phone,
            contractorDetails?.email ? `Email: ${contractorDetails.email}` : ""
          ),
          tenant: tenantDetails
            ? `${tenantDetails.fullName || ""}${tenantDetails.businessName ? " (" + tenantDetails.businessName + ")" : ""}`
            : "",
          workDescription: projectDetail?.workDescription || "",
          bin: buildingDetails?.BIN || "",
          block: buildingDetails?.block || "",
          borough: buildingDetails?.borough || "",
          cb: buildingDetails?.cb || "",
          houseNumber: buildingDetails?.houseNumber || "",
          lot: buildingDetails?.lot || "",
          workTypes: adjustedWorkTypes,
          status: projectDetail?.notes || "",
          assignedTo: groupUsers.find(u => u.id === projectDetail?.assignedTo)?.name || null,
          companyName: groupInfo?.groupName || null,
        };

        setSheetData(sheetDataResult);
      } catch (error) {
        console.error("Error generating sheet data:", error);
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        generateSheetData();
      }, { timeout: 1000 });
    } else {
      setTimeout(generateSheetData, 0);
    }
  }, [projectDetail, buildingDetails, ownerDetails, workTypes, groupUsers, groupId, getGroupInfo]);


  // const getBuildingInfo = useCallback(async (buildingId) => {
  //   if (!buildingId) {
  //     return;
  //   }

  //   const cacheKey = `buildingInfo_${buildingId}`;
  //   if (buildingInfoCache.has(cacheKey)) {
  //     setProjectBuildingDetails(buildingInfoCache.get(cacheKey));
  //     return;
  //   }

  //   try {
  //     const buildingRef = doc(db, "buildings", buildingId);
  //     const buildingSnap = await getDoc(buildingRef);

  //     if (!buildingSnap.exists()) {
  //       console.warn(`No building found with ID: ${buildingId}`);
  //       return;
  //     }

  //     const buildingData = buildingSnap.data();
  //     let ownerName = "";

  //     if (buildingData.owner) {
  //       const ownerRef = doc(db, "contacts", buildingData.owner);
  //       const ownerSnap = await getDoc(ownerRef);

  //       if (ownerSnap.exists()) {
  //         ownerName = ownerSnap.data().fullName;
  //       }
  //     }

  //     const buildingInfo = {
  //       ...buildingData,
  //       owner: ownerName,
  //     };

  //     buildingInfoCache.set(cacheKey, buildingInfo);
  //     setProjectBuildingDetails(buildingInfo);
  //   } catch (error) {
  //     console.error("Error fetching building details:", error);
  //   }
  // }, []);

  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [failureOpen, setOpenFailure] = useState(false);
  const [duplicateOpen, setduplicateOpen] = useState(false);
  const [SubmitLoader, setSubmitLoader] = useState(false);
  let location = useLocation();

  const openSuccess = () => {
    setOpen(true);
  };

  const openFailure = () => {
    setOpenFailure(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleFailureClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenFailure(false);
  };

  const handleduplicateOpen = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setduplicateOpen(false);
  };

  const handleChangeTab = (event, newValue) => {
    setTab(newValue);
  };

  const onSubmit = useCallback(async () => {
    setSubmitLoader(true);

    if (!projectDetail) {
      setSubmitLoader(false);
      return false;
    }

    const building = buildingsCacheRef.current.get(projectDetail.building) || {};
    const buildingString_lc = building.customerReference_lc
      || (building.customerReference || '').trim().toLowerCase();
    const rawAddress = `${building.houseNumber || ''} ${building.streetName || ''}`.trim();
    const buildingAddress_lc = rawAddress.toLowerCase();

    const normalizedCustomerReference = (projectDetail.customerReference || "")
      .trim()
      .toLowerCase();
    const thisProject = doc(db, "projects", projectId);

    try {
      const updatedProject = await updateLowercaseFields(projectDetail);

      // Check for duplicates
      const checkDuplicate = async () => {
        const q = query(
          collection(db, "projects"),
          where("groupId", "==", groupId),
          where("customerReference_lc", "==", normalizedCustomerReference)
        );

        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.find(doc =>
          doc.id !== projectId
        );
      };

      const duplicate = await checkDuplicate();

      if (!duplicate) {
        const currentProjectSnap = await getDoc(thisProject);
        const currentProjectData = currentProjectSnap.data();

        let kanbanColumnId = currentProjectData?.kanbanColumnId;

        if (projectDetail.assignedTo && !currentProjectData?.assignedTo) {
          kanbanColumnId = 1;
        } else if (!projectDetail.assignedTo && currentProjectData?.assignedTo) {
          kanbanColumnId = null;
        }

        const currentTimestamp = Date.now();
        const projectUpdateData = {
          ...projectDetail,
          flaggedMessage: projectDetail.flaggedMessage || "This job requires attention",
          customerReference: projectDetail.customerReference.trim(),
          dateCreated: projectDetail.dateCreated || serverTimestamp(),
          dateUpdated: currentTimestamp,
          customerReference_lc: projectDetail.customerReference.trim().toLowerCase(),
          buildingString_lc,
          buildingAddress_lc,
          status_lc: (projectDetail.status || '').trim().toLowerCase(),
          aka_lc: (projectDetail.aka || '').trim().toLowerCase(),
          client_lc: (clientDetails?.businessName || '').trim().toLowerCase(),
          tenant_lc: (tenantDetails?.businessName || '').trim().toLowerCase(),
          applicant_lc: (projectDetail.applicantName || '').trim().toLowerCase(),
          filingRep_lc: (projectDetail.filingRepName || '').trim().toLowerCase(),
          assignedTo_lc: (assignedToName || '').trim().toLowerCase(),
          kanbanColumnId: kanbanColumnId !== undefined ? kanbanColumnId : null,
        };

        await setDoc(thisProject, projectUpdateData, { merge: true });

        // Update invoices in background
        setTimeout(async () => {
          try {
            const invoicesQ = query(
              collection(db, "invoices"),
              where("invoiceJob", "==", projectId)
            );
            const invoicesSnap = await getDocs(invoicesQ);
            if (!invoicesSnap.empty) {
              const invBatch = writeBatch(db);
              const normalizedJobLc = projectDetail.customerReference.trim().toLowerCase();
              invoicesSnap.forEach(invDoc => {
                invBatch.update(invDoc.ref, {
                  invoiceJob_lc: normalizedJobLc,
                });
              });
              await invBatch.commit();
            }
          } catch (error) {
            console.error("Error updating invoices:", error);
          }
        }, 0);

        // Sync task
        await syncTaskForJob({
          ...projectDetail,
          kanbanColumnId: kanbanColumnId
        });

        // Clear cache for this project since it's been updated
        projectDataCache.delete(`project_${projectId}`);
        projectDataCache.delete(`lowercase_${projectId}`);

        setIsDirty(false);
        openSuccess();
        return true;
      } else {
        setduplicateOpen(true);
        return false;
      }
    } catch (error) {
      console.error("Error:", error);
      openFailure();
      return false;
    } finally {
      setSubmitLoader(false);
    }
  }, [projectDetail, projectId, groupId, clientDetails, tenantDetails, assignedToName, updateLowercaseFields, syncTaskForJob]);

  const handleDateChange = useCallback((fieldName, value) => {
    if (!projectDetail) return;

    setIsDirty(true);
    setProjectDetail(prev => ({
      ...prev,
      [fieldName]: JSON.stringify(value)
    }));
  }, [projectDetail]);

  const saveAndClose = async () => {
    const isSuccess = await onSubmit();

    if (isSuccess) {
      navigate("/projects", { state: { feedback: { message: 'Project saved successfully!', severity: 'success' } } });
    } else {
      setDialogOpen(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setProjectDetail(prev => ({
      ...prev,
      [name]: value
    }));
    setIsDirty(true);

    if (name === "building") {
      // Remove setTimeout - call directly
      getBuildingInfo(value);
    }
  }, [getBuildingInfo]);
  const discardAndClose = () => {
    navigate("/projects");
  };

  const handleChangeNotes = useCallback((notes) => {
    setProjectDetail(prev => ({ ...prev, notes: notes }));
  }, []);

  const handleChangeDocuments = useCallback((data) => {
    setProjectDetail(prev => ({ ...prev, documents: data }));
  }, []);

  const handleCloseProject = () => {
    if (isDirty && !props.isInactive) {
      setDialogOpen(true);
      return;
    }

    if (location?.state?.fromBuildingReferencesJobDetails?.fromBuildingReferencesJob) {
      navigate(`/buildings/details/${location?.state?.fromBuildingReferencesJobDetails.buildingId}`, { state: { FromJobDetailsReferencesBuilding: true } });
    } else {
      navigate("/projects");
    }
  };

  const hasRequiredFields = () => {
    return !!projectDetail?.customerReference;
  };

  const handleFlagCheckboxChange = useCallback((event) => {
    setIsDirty(true);
    setProjectDetail(prev => ({
      ...prev,
      flagged: event.target.checked
    }));
  }, []);

  const handleFlaggedTextChange = useCallback((event) => {
    setIsDirty(true);
    const newValue = event.target.value;
    setFlaggedMessage(newValue);
    setProjectDetail(prev => ({
      ...prev,
      flaggedMessage: newValue
    }));
  }, []);

  const handleOpenModal = useCallback((option = null, currentOptionNameId, currentOptionLabel) => {
    setEditingOption(option);
    setUserDefinedOption(option ? option.value : "");
    setCurrentOptionFieldDisplayed({ name: currentOptionNameId, label: currentOptionLabel });
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditingOption(null);
    setUserDefinedOption("");
    setOpenModal(false);
  }, []);

  const handleAddOrEditUserDefinedValue = useCallback(async (nameId) => {
    if (!userDefinedOption.trim() || !userId) return;

    try {
      const profilesSnapshot = await getDocs(
        query(collection(db, "profiles"), where("userId", "==", userId))
      );

      if (!profilesSnapshot.empty) {
        const userProfileRef = doc(db, "profiles", profilesSnapshot.docs[0].id);
        const userProfileData = profilesSnapshot.docs[0].data();
        const currentOptions = userProfileData.userDefinedOptions || [];

        let updatedOptions;
        if (editingOption) {
          updatedOptions = currentOptions.filter(
            opt => !(opt.name === editingOption.name && opt.value === editingOption.value)
          );
          updatedOptions.push({ name: nameId, value: userDefinedOption });
        } else {
          updatedOptions = [...currentOptions, { name: nameId, value: userDefinedOption }];
        }

        await updateDoc(userProfileRef, {
          userDefinedOptions: updatedOptions
        });
      }

      handleCloseModal();
    } catch (error) {
      console.error("Error saving option: ", error);
    }
  }, [userDefinedOption, editingOption, userId, handleCloseModal]);

  const handleChangeDetails = useCallback(async (e) => {
    const newId = e.target.value;
    if (newId === projectId || isSwitchingRef.current) return;

    setLoadingJob(true);

    if (isDirty && !props.isInactive) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Do you want to save them before switching?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Discard',
      });

      if (result.isConfirmed) {
        const ok = await onSubmit();
        if (!ok) {
          setLoadingJob(false);
          return;
        }
      }
    }

    navigate(`/projects/details/${newId}`, { replace: true });

    setTimeout(() => {
      setProjectDetail(null);
      setProjectBuildingDetails(null);
      setWorkTypes([]);
      setOwnerDetails(null);
      setSheetData(null);

      // Load new project data
      getProject(newId);
      setLoadingJob(false);
    }, 50);
  }, [projectId, isDirty, props.isInactive, onSubmit, navigate, getProject]);

  const handleDeleteOption = useCallback((optionToDelete, stateSetter, stateData) => {
    if (stateSetter && stateData) {
      stateSetter(prevOptions =>
        prevOptions.filter((option) => option.value !== optionToDelete.value)
      );
    }

    setTimeout(async () => {
      try {
        const profilesSnapshot = await getDocs(
          query(collection(db, "profiles"), where("userId", "==", userId))
        );

        if (!profilesSnapshot.empty) {
          profilesSnapshot.docs.forEach(async (doc) => {
            const data = doc.data();
            if (data?.userDefinedOptions) {
              const updatedOptions = data.userDefinedOptions.filter(
                (option) =>
                  option.name !== optionToDelete.name || option.value !== optionToDelete.value
              );

              await updateDoc(doc.ref, {
                userDefinedOptions: updatedOptions
              });
            }
          });
        }
      } catch (error) {
        console.error("Error deleting option from Firestore: ", error);
      }
    }, 0);
  }, [userId]);

  if (loadingJob) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!projectDetail) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={2} sx={{ marginBottom: "-55px", justifyContent: "flex-end" }}>
        <Grid item xs={12} md={6} lg={4}>
          <InfiniteScrollSelect
            disableClear={true}
            collectionName="projects"
            label="customerReference"
            onChange={handleChangeDetails}
            value={projectDetail?.customerReference}
            name="customerReference"
            inputLabel="Switch to another Job"
          />
        </Grid>
      </Grid>

      <Modal open={openModal} onClose={handleCloseModal}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 20,
          }}
        >
          <h2>{editingOption ? "Edit Option" : "Add Option"}</h2>

          <TextField
            label={currentOptionFieldDisplayed.label || ""}
            variant="outlined"
            value={userDefinedOption}
            onChange={(e) => setUserDefinedOption(e.target.value)}
            fullWidth
            required
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => handleAddOrEditUserDefinedValue(currentOptionFieldDisplayed.name)}
            style={{ marginTop: 20 }}
          >
            {editingOption ? "Save Changes" : "Add Option"}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleCloseModal}
            style={{ marginTop: 20, marginLeft: "1rem" }}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      <Typography sx={{ textTransform: "uppercase", display: "flex", alignItems: "center" }} align="left" variant="h4">
        {projectDetail.flagged && (
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: "red",
              borderRadius: "50%",
              display: "inline-block",
              marginRight: 1,
            }}
          />
        )}
        {projectDetail.customerReference}
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider", paddingLeft: 0 }}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          aria-label="tab navigation"
        >
          <Tab label="Details" />
          <Tab label="Work Types" />
          <Tab label="Inspections" />
          <Tab label="Documents" />
          <Tab label="DOB status" />
          <Tab label="Notes" />
          <Tab label="Forms" />
        </Tabs>
      </Box>
      <TabPanel value={tab} index={0}>


        <Grid container spacing={2}>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={projectDetail.flagged ? projectDetail.flagged : false}
                  onChange={handleFlagCheckboxChange}
                />
              }
              label="Flag this Job"
            />
          </Grid>

          {projectDetail.flagged && (
            <Grid item xs={12}>
              <TextField
                label="Flagged Message"
                variant="outlined"
                value={flaggedMessage}
                onChange={handleFlaggedTextChange}
                fullWidth
                InputProps={{
                  style: { color: "red" },
                }}

              />
            </Grid>
          )}


          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Job ID"
              variant="outlined"
              name="customerReference"
              value={projectDetail.customerReference}
              onChange={handleChange}
              fullWidth
              required

            />
          </Grid>


          <Grid item xs={12} md={6} lg={4}>

            <div style={{ position: "relative" }}>
              <div
                className="addFromAutofillContactSection"
                onClick={() => handleOpenModal(null, "type", "Type")}
              >
                <AddIcon />
              </div>

              <TextField
                select
                label="Type"
                variant="outlined"
                name="type"
                value={projectDetail.type || ""}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  endAdornment: projectDetail.type && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange({ target: { name: "type", value: "" } });
                      }}
                      style={{ marginRight: 20 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              >
                {["New Building (NB)",
                  "Big Alt",
                  "Alteration Type 1 (Alt 1)",
                  "Alteration Type 2 (Alt 2)",
                  "Alteration Type 3 (Alt 3)",
                  "Place of Assembly (PA)",
                  "Sign (SG)",].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}

                {projectDetail.type &&
                  ![
                    "New Building (NB)",
                    "Big Alt",
                    "Alteration Type 1 (Alt 1)",
                    "Alteration Type 2 (Alt 2)",
                    "Alteration Type 3 (Alt 3)",
                    "Place of Assembly (PA)",
                    "Sign (SG)",
                    ...typeFieldOptions.map((option) => option.value)
                  ].includes(projectDetail.type) && (
                    <MenuItem key={projectDetail.type} value={projectDetail.type} className="menu-item-options">
                      {projectDetail.type}
                    </MenuItem>
                  )}
                {typeFieldOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value} className="menu-item-options" >
                    {option.value}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(option, "type", "Type");
                      }}
                      style={{ marginLeft: "auto" }}
                      className="edit-icon-inside-options"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    {option.createdByCurrentUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOption(option, setTypeFieldOptions, typeFieldOptions);
                        }}
                        style={{ marginLeft: 10 }}
                        className="edit-icon-inside-options"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}

                  </MenuItem>
                ))}

              </TextField>

            </div>

          </Grid>




          <Grid item xs={12} md={6} lg={4}>

            <div style={{ position: "relative" }}>
              <div
                className="addFromAutofillContactSection"
                onClick={() => handleOpenModal(null, "aka", "AKA")}
              >
                <AddIcon />
              </div>

              <TextField
                select
                label="AKA"
                variant="outlined"
                name="aka"
                value={projectDetail.aka || ""}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  endAdornment: projectDetail.aka && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange({ target: { name: "aka", value: "" } });
                      }}
                      style={{ marginRight: 20 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>


                  ),
                }}
              >

                {projectDetail.aka &&
                  !akaFieldOptions?.map((option) => option.value).includes(projectDetail.aka) && (
                    <MenuItem key={projectDetail.aka} value={projectDetail.aka}>
                      {projectDetail.aka}
                    </MenuItem>
                  )}
                {akaFieldOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value} className="menu-item-options" >
                    {option.value}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(option, "aka", "AKA");
                      }}
                      style={{ marginLeft: "auto" }}
                      className="edit-icon-inside-options"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>


                    {option.createdByCurrentUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOption(option, setAkaFieldOptions, akaFieldOptions);
                        }}
                        style={{ marginLeft: 10 }}
                        className="edit-icon-inside-options"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}

                  </MenuItem>
                ))}




              </TextField>

            </div>

          </Grid>



          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Status"
              variant="outlined"
              name="status"
              value={projectDetail.status}
              onInput={handleChange}
              fullWidth

            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Date"
              variant="outlined"
              name="date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={projectDetail.date}
              onInput={handleChange}
              fullWidth

            />
          </Grid>



          <Grid item xs={12} md={6} lg={4}>
            <TextField
              name="DOBNumber"
              label="DOB reference (BIS or Now!)"
              variant="outlined"
              value={projectDetail.DOBNumber || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>



          <Grid item xs={12} md={6} lg={4}>
            {/* <TextField
                  label={"Project manager"}
                  name="projectManager"
                  onChange={handleChange}
                  select
                  value={projectDetail.projectManager}
                  fullWidth
                >
                  {contacts.map((contact) => (
                    <MenuItem value={contact.id}>{contact.fullName}</MenuItem>
                  ))}
                </TextField> */}
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={handleChange}
              value={projectDetail.projectManager}
              name="projectManager"
              inputLabel="Project manager"
            />
          </Grid>



          {currentSubscription?.isPro && (
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                label="Assigned To"
                name="assignedTo"
                select
                onChange={handleChange}
                value={projectDetail.assignedTo || ""}
                fullWidth
                InputProps={{
                  endAdornment: projectDetail.assignedTo ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          handleChange({ target: { name: 'assignedTo', value: '' } })
                        }
                        style={{ marginRight: '10px' }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              >
                {groupUsers.length > 0 ? (
                  groupUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No users available</MenuItem>
                )}
              </TextField>
            </Grid>
          )}



        </Grid>





        <Typography align="left" variant="h4" sx={{ fontWeight: "bold", color: "#111715", marginTop: "30px", marginBottom: "20px" }}>
          Building Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            {/* <TextField
                  label={"Building"}
                  name="building"
                  select
                  onChange={handleChange}
                  value={projectDetail.building}
                  fullWidth
                >
                  {buildings.map((building) => (
                    <MenuItem value={building.id}>
                      {building.customerReference}
                    </MenuItem>
                  ))}
                </TextField> */}
            <InfiniteScrollSelect
              collectionName="buildings"
              label="customerReference"
              onChange={handleChange}
              value={projectDetail.building}
              name="building"
              inputLabel="Building"
            />
          </Grid>


        </Grid>


        {projectDetail?.building && (
          <>
            <Grid container spacing={3} marginTop={3}>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  Name:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.customerReference || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  Borough:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.borough || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  Owner:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.owner || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  BIN:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.BIN || "N/A"}
                </Typography>
              </Grid>
            </Grid>


            <Typography
              variant="h4"
              sx={{ fontWeight: 'bold', color: "#111715", marginTop: "30px", marginBottom: "20px", fontSize: '1.5rem', paddingLeft: "5px" }}
            >
              Address Details
            </Typography>

            <Grid container spacing={3}>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  House Number:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.houseNumber || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  Street Name:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.streetName || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  Special Place Name:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.specialPlaceName || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  City:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.city || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  State:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.state || "N/A"}
                </Typography>
              </Grid>


              <Grid item xs={12} md={6} lg={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: '#111715', fontSize: '1rem' }}
                >
                  ZIP:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  {projectBuildingDetails?.ZIP || "N/A"}
                </Typography>
              </Grid>
            </Grid>


          </>
        )}




        <Typography align="left" variant="h4" sx={{ fontWeight: "bold", color: "#111715", marginTop: "30px", marginBottom: "20px" }}>
          Job Contacts
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            {/* <TextField
                  label={"Client"}
                  name="client"
                  onChange={handleChange}
                  value={projectDetail.client}
                  select
                  fullWidth
                >
                  {contacts.map((contact) => (
                    <MenuItem value={contact.id}>{contact.fullName}</MenuItem>
                  ))}
                </TextField> */}
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={handleChange}
              value={projectDetail.client}
              name="client"
              inputLabel="Client"
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={handleChange}
              value={projectDetail.tenant}
              inputLabel="Tenant"
              name="tenant"
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={async (e) => {
                const updated = {
                  ...projectDetail,
                  applicant: e.target.value,
                  applicantName: contacts.find(c => c.id === e.target.value)?.fullName || ""
                };
                const withLowercase = await updateLowercaseFields(updated);
                setIsDirty(true);
                setProjectDetail(withLowercase);
              }}
              value={projectDetail.applicant}
              name="applicant"
              inputLabel="Applicant"
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={handleChange}
              value={projectDetail.contractor}
              name="contractor"
              inputLabel="Contractor"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={async (e) => {
                const updated = {
                  ...projectDetail,
                  filingRep: e.target.value,
                  filingRepName: contacts.find(c => c.id === e.target.value)?.fullName || ""
                };
                const withLowercase = await updateLowercaseFields(updated);
                setIsDirty(true);
                setProjectDetail(withLowercase);
              }}
              value={projectDetail.filingRep || ""}
              name="filingRep"
              inputLabel="Filing representative"
            />
          </Grid>
        </Grid>



        <Typography align="left" variant="h4" sx={{ fontWeight: "bold", color: "#111715", marginTop: "30px", marginBottom: "20px" }}>
          Additional Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Landmark Status"
              variant="outlined"
              name="landmarkStatus"
              value={projectDetail.landmarkStatus}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Little E"
              variant="outlined"
              name="littleE"
              value={projectDetail.littleE}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Loft Law"
              variant="outlined"
              name="loftLaw"
              value={projectDetail.loftLaw}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>


          <Grid item xs={12} md={6} lg={4}>

            <div style={{ position: "relative" }}>
              <div
                className="addFromAutofillContactSection"
                onClick={() => handleOpenModal(null, "ACPReport", "ACP Report")}
              >
                <AddIcon />
              </div>

              <TextField
                select
                label="ACP Report"
                variant="outlined"
                name="ACPReport"
                value={projectDetail.ACPReport || ""}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  endAdornment: projectDetail.ACPReport && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange({ target: { name: "ACPReport", value: "" } });
                      }}
                      style={{ marginRight: 20 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              >
                {[
                  "ACP-21",
                  "ACP5",
                  "ACP5 (by client)",
                  "ACP5 by owner",
                  "ACP-5 Carmelo",
                  "ACP-5 Robert",
                  "ASB-4",
                  "N/A",
                  "Other",
                  "Owner"
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}

                {projectDetail.ACPReport &&
                  ![
                    "ACP-21",
                    "ACP5",
                    "ACP5 (by client)",
                    "ACP5 by owner",
                    "ACP-5 Carmelo",
                    "ACP-5 Robert",
                    "ASB-4",
                    "N/A",
                    "Other",
                    "Owner",
                    ...acpReportFieldOptions?.map((option) => option.value)
                  ].includes(projectDetail.ACPReport) && (
                    <MenuItem key={projectDetail.ACPReport} value={projectDetail.ACPReport} className="menu-item-options">
                      {projectDetail.ACPReport}
                    </MenuItem>
                  )}

                {acpReportFieldOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value} className="menu-item-options" >
                    {option.value}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(option, "ACPReport", "ACP Report");
                      }}
                      style={{ marginLeft: "auto" }}
                      className="edit-icon-inside-options"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    {option.createdByCurrentUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOption(option, setAcpReportFieldOptions, acpReportFieldOptions);
                        }}
                        style={{ marginLeft: 10 }}
                        className="edit-icon-inside-options"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}


                  </MenuItem>
                ))}

              </TextField>

            </div>

          </Grid>





          <Grid item xs={12} md={6} lg={4}>

            <div style={{ position: "relative" }}>
              <div
                className="addFromAutofillContactSection"
                onClick={() => handleOpenModal(null, "selfCertification", "self Cert")}
              >
                <AddIcon />
              </div>

              <TextField
                select
                label="self Cert"
                variant="outlined"
                name="selfCertification"
                value={projectDetail.selfCertification || ""}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  endAdornment: projectDetail.selfCertification && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange({ target: { name: "selfCertification", value: "" } });
                      }}
                      style={{ marginRight: 20 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              >
                {projectDetail.selfCertification &&
                  ![
                    ...selfCertificationFieldOptions?.map((option) => option.value)
                  ].includes(projectDetail.selfCertification) && (
                    <MenuItem key={projectDetail.selfCertification} value={projectDetail.selfCertification} className="menu-item-options">
                      {projectDetail.selfCertification}
                    </MenuItem>
                  )}

                {selfCertificationFieldOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value} className="menu-item-options" >
                    {option.value}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(option, "selfCertification", "self Cert");
                      }}
                      style={{ marginLeft: "auto" }}
                      className="edit-icon-inside-options"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>


                    {option.createdByCurrentUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOption(option, setSelfCertificationFieldOptions, selfCertificationFieldOptions);
                        }}
                        style={{ marginLeft: 10 }}
                        className="edit-icon-inside-options"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}

                  </MenuItem>
                ))}

              </TextField>

            </div>

          </Grid>



          <Grid item xs={12} md={6} lg={4}>

            <div style={{ position: "relative" }}>
              <div
                className="addFromAutofillContactSection"
                onClick={() => handleOpenModal(null, "reconReqd", "Recon Required")}
              >
                <AddIcon />
              </div>

              <TextField
                select
                label="Recon Required"
                variant="outlined"
                name="reconReqd"
                value={projectDetail.reconReqd || ""}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  endAdornment: projectDetail.reconReqd && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange({ target: { name: "reconReqd", value: "" } });
                      }}
                      style={{ marginRight: 20 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              >
                {projectDetail.reconReqd &&
                  ![
                    ...reconReqdFieldOptions?.map((option) => option.value)
                  ].includes(projectDetail.reconReqd) && (
                    <MenuItem key={projectDetail.reconReqd} value={projectDetail.reconReqd} className="menu-item-options">
                      {projectDetail.reconReqd}
                    </MenuItem>
                  )}

                {reconReqdFieldOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value} className="menu-item-options" >
                    {option.value}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(option, "reconReqd", "Recon Required");
                      }}
                      style={{ marginLeft: "auto" }}
                      className="edit-icon-inside-options"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>


                    {option.createdByCurrentUser && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOption(option, setReconReqdFieldOptions, reconReqdFieldOptions);
                        }}
                        style={{ marginLeft: 10 }}
                        className="edit-icon-inside-options"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}


                  </MenuItem>
                ))}

              </TextField>

            </div>

          </Grid>



          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Proposal Sent"
              variant="outlined"
              name="proposalSent"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={projectDetail.proposalSent}
              onInput={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Proposal Received"
              variant="outlined"
              name="proposalRecd"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={projectDetail.proposalRecd}
              onInput={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Floor(s)"
              variant="outlined"
              name="floors"
              value={projectDetail.floors}
              onChange={handleChange}
              fullWidth

            />
          </Grid>


          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Apt./Condo No(s)"
              variant="outlined"
              name="apt"
              value={projectDetail.apt}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth

            />
          </Grid>


          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Tracking Number"
              variant="outlined"
              name="trackingNumber"
              value={projectDetail.trackingNumber}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth

            />
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            <TextField
              label="Work Description"
              variant="outlined"
              multiline
              rows="6"
              name="workDescription"
              value={projectDetail.workDescription}
              onInput={handleChange}
              fullWidth
            />
          </Grid>


        </Grid>













































        <Box
          sx={{
            position: "fixed",
            bottom: "1rem",
            right: "1rem",
            "& > :not(style)": { m: 1 },
          }}
        >
          {!props.isInactive && (
            <>
              <Fab
                color="primary"
                variant="extended"
                onClick={handleModalOpen}
              >
                <DownloadForOfflineIcon sx={{ mr: 1 }} />
                Generate Job Sheet
              </Fab>
              <Fab
                color="primary"
                aria-label="add"
                variant="extended"
                disabled={SubmitLoader || (!isDirty && !hasRequiredFields()) || props.isInactive}
                form="projectForm"
                onClick={onSubmit}
              >
                <Save sx={{ mr: 1 }} />
                Save
              </Fab>
            </>
          )}

          <Fab
            color="secondary"
            aria-label="add"
            variant="extended"
            onClick={handleCloseProject}
          >
            <Close sx={{ mr: 1 }} />
            Close
          </Fab>

        </Box>
        <Snackbar open={open} autoHideDuration={2000} onClose={handleClose}>
          <Alert
            onClose={handleClose}
            severity="success"
            sx={{ width: "100%" }}
          >
            Project successfully saved!
          </Alert>
        </Snackbar>
        <Snackbar
          open={failureOpen}
          autoHideDuration={2000}
          onClose={handleFailureClose}
        >
          <Alert
            onClose={handleFailureClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            Something went wrong!
          </Alert>
        </Snackbar>
        <Snackbar
          open={duplicateOpen}
          autoHideDuration={2000}
          onClose={handleduplicateOpen}
        >
          <Alert
            onClose={handleduplicateOpen}
            severity="error"
            sx={{ width: "100%" }}
          >
            Job ID already exists!
          </Alert>
        </Snackbar>
        <SaveCloseDialog
          open={dialogOpen}
          saveAndClose={saveAndClose}
          discardAndClose={discardAndClose}
          setDialogOpen={setDialogOpen}
        />

        <div>
          <JobSheetModal sheetData={sheetData} open={modalOpen} onClose={handleModalClose} />
        </div>
      </TabPanel>



      <TabPanel value={tab} index={1}>
        <WorkTypes
          userId={projectDetail.userId}
          db={db}
          projectId={projectId}
          projectDOB={projectDetail.DOBNumber}
          contacts={contacts}
          isInactive={props.isInactive}
        />
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Inspections
          userId={projectDetail.userId}
          db={db}
          projectId={projectId}
          contacts={contacts}
          // invoices={invoices}
          groupId={groupId}
          isPro={isPro}
          isInactive={props.isInactive}
        />
      </TabPanel>




      <TabPanel value={tab} index={3}>
        <Documents
          documents={projectDetail?.documents || []}
          projectId={projectId}
          onSubmit={onSubmit}
          getProject={getProject}
          projectData={projectDetail}
          isInactive={props.isInactive}
          contacts={contacts}
        />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        {!props.isInactive ? (
          <div style={{ height: "calc(100vh - 230px)" }}>
            <ProjectStatus
              thisProject={projectDetail.DOBNumber}
              workTypeIDs={workTypes.map((workType) => workType.dobNumber)}
            />
          </div>
        ) : (
          <div style={{ height: "calc(100vh - 230px)" }}>
            This feature is available with an active subscription.
          </div>
        )}
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <Notes
          notes={projectDetail.notes}
          handleChangeNotes={handleChangeNotes}
          onSubmit={onSubmit}
          isInactive={props.isInactive}
        />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        {!buildingDetails || !contactsLoaded ? ( // Change from contactsLoading to !contactsLoaded
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Forms
            projectId={projectId}
            applicantDetails={applicantDetails}
            filingRepDetails={filingRepDetails}
            buildingDetails={{ ...buildingDetails, DOBNumber: projectDetail.DOBNumber }}
            extraFieldsToAutoFill={{ jobNumber: projectDetail.customerReference, apt: projectDetail.apt, floors: projectDetail.floors }}
            ownerDetails={ownerDetails}
            userId={projectDetail.userId}
            db={db}
            groupId={groupId}
            isInactive={props.isInactive}
          />
        )}
      </TabPanel>
    </>
  );
};
